import React, { forwardRef } from 'react';
import c from 'classnames';
import { FieldError } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Typography } from '../Typography/Typography';
import { Icon } from '../Icon/Icon';
import styles from './RadioGroup.module.css';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  options: RadioOption[];
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  label?: string;
  labelClasses?: string;
  // Accept error objects from any react-hook-form schema without requiring
  // callers to specify generics on this UI component.
  error?: FieldError;
  hideErrors?: boolean;
  description?: string;
  classes?: string;
  classNames?: string;
  showRequiredIndicator?: boolean;
  disabled?: boolean;
  required?: boolean;
}

export const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(
  (
    {
      name,
      options,
      value,
      onChange,
      onBlur,
      label,
      labelClasses,
      error,
      hideErrors,
      description,
      classes,
      classNames,
      disabled,
    },
    ref,
  ) => {
    return (
      <div className={c(styles.radioGroup, classes, classNames)}>
        {label && (
          <label className={c(styles.label, labelClasses)}>
            <Typography variant='body-s'>{label}</Typography>
          </label>
        )}
        {!!description && (
          <Typography variant='label' className={styles.description}>
            {description}
          </Typography>
        )}
        <div className={styles.optionsContainer}>
          {options.map((option, index) => (
            <label
              key={option.value}
              className={c(styles.radioLabel, {
                [styles.disabled]: disabled || option.disabled,
              })}
            >
              <input
                ref={index === 0 ? ref : undefined} // Only attach ref to first input
                type='radio'
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled || option.disabled}
                className={styles.radioInput}
              />
              <span className={styles.radioText}>{option.label}</span>
            </label>
          ))}
        </div>
        {!hideErrors && (
          <div className={styles.errors}>
            {!!error && (
              <ErrorMessage
                errors={{ [name]: error }}
                name={name}
                render={({ message }: { message: string }) => (
                  <div
                    className={styles.error}
                    id={`error-${name}`}
                    key={message}
                    role='alert'
                  >
                    <Icon name='alertCircle' size='xs' /> {message}
                  </div>
                )}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
